import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './SchoolSignup.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const STUDENT_COUNT_OPTIONS = ['0-50', '50-100', '100+'];
const STUDENT_TYPES_OPTIONS = ['BAC', 'BEM', 'Middle', 'Primary'];

const SupportSchoolSignup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    schoolName: '',
    city: 'Algeria',
    directorName: '',
    phone: '',
    email: '',
    password: '',
    studentCountBucket: '',
    studentTypes: [],
    termsAccepted: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const toggleStudentType = (val) => {
    setForm((prev) => ({
      ...prev,
      studentTypes: prev.studentTypes.includes(val)
        ? prev.studentTypes.filter((t) => t !== val)
        : [...prev.studentTypes, val]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.schoolName?.trim()) {
      setError('School name is required');
      return;
    }
    if (!form.directorName?.trim()) {
      setError('Director name is required');
      return;
    }
    if (!form.phone?.trim()) {
      setError('Phone number is required');
      return;
    }
    if (!form.email?.trim()) {
      setError('Email is required');
      return;
    }
    if (!form.password || form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!form.termsAccepted) {
      setError('You must agree to the platform terms');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/schools/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'support',
          schoolName: form.schoolName.trim(),
          city: form.city.trim() || 'Algeria',
          directorName: form.directorName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          password: form.password,
          studentCountBucket: form.studentCountBucket || null,
          studentTypes: form.studentTypes.length ? form.studentTypes : null,
          termsAccepted: true
        })
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || 'Registration failed');
        setLoading(false);
        return;
      }

      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify({ ...data.user, role: 'school_admin' }));
        navigate('/business/dashboard/support', { replace: true });
      } else {
        setError('Registration succeeded but invalid response');
      }
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="school-signup-page">
      <nav className="school-signup-nav">
        <Link to="/business" className="back-link">
          <span className="material-icons">arrow_back</span> Back to Business
        </Link>
        <Link to="/" className="logo-link">
          <span className="material-icons">school</span> Syllera
        </Link>
      </nav>

      <div className="school-signup-container">
        <h1 className="school-signup-title">Create Your Support School Account</h1>
        <p className="school-signup-subtitle">Manage your support classes and students in one place.</p>

        <form onSubmit={handleSubmit} className="school-signup-form">
          {error && (
            <div className="school-signup-error">
              <span className="material-icons">error_outline</span> {error}
            </div>
          )}

          <label className="school-signup-label">School Name *</label>
          <input
            type="text"
            name="schoolName"
            value={form.schoolName}
            onChange={handleChange}
            placeholder="e.g. Centre de Soutien Batna"
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">City</label>
          <input
            type="text"
            name="city"
            value={form.city}
            onChange={handleChange}
            placeholder="Algeria"
            className="school-signup-input"
          />

          <label className="school-signup-label">Director Name *</label>
          <input
            type="text"
            name="directorName"
            value={form.directorName}
            onChange={handleChange}
            placeholder="Full name"
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">Phone Number *</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="+213 ..."
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">Email *</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="school@example.com"
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">Password * (min 8 characters)</label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            placeholder="••••••••"
            className="school-signup-input"
            minLength={8}
            required
          />

          <label className="school-signup-label">Number of Students</label>
          <select
            name="studentCountBucket"
            value={form.studentCountBucket}
            onChange={handleChange}
            className="school-signup-input"
          >
            <option value="">Select range</option>
            {STUDENT_COUNT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>

          <label className="school-signup-label">Type of Students</label>
          <div className="school-signup-chips">
            {STUDENT_TYPES_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`chip ${form.studentTypes.includes(opt) ? 'active' : ''}`}
                onClick={() => toggleStudentType(opt)}
              >
                {opt}
              </button>
            ))}
          </div>

          <label className="school-signup-checkbox-label">
            <input
              type="checkbox"
              name="termsAccepted"
              checked={form.termsAccepted}
              onChange={handleChange}
            />
            <span>I agree to the platform terms</span>
          </label>

          <button type="submit" className="school-signup-submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create My School Dashboard'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SupportSchoolSignup;
